import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { filesService } from '@/services/land/filesService'
import type { LandFile, FileCategory } from '@popety-io/types'

interface FilesContextState {
  // Data
  files: LandFile[]
  loading: boolean
  error: string | null
  total: number
  categories: { category: FileCategory; count: number }[]

  // Filters
  searchTerm: string
  setSearchTerm: (term: string) => void
  activeCategory: FileCategory | 'all'
  setActiveCategory: (category: FileCategory | 'all') => void

  // Actions
  fetchFiles: (landId: string) => Promise<void>
  refreshFiles: () => Promise<void>
  uploadFile: (
    landId: string,
    file: File,
    description?: string
  ) => Promise<void>
  deleteFile: (fileId: string) => Promise<void>
  downloadFile: (fileId: string) => Promise<void>

  // Current land ID
  currentLandId: string | null
}

const FilesContext = createContext<FilesContextState | undefined>(undefined)

export const useFiles = () => {
  const context = useContext(FilesContext)
  if (!context) {
    throw new Error('useFiles must be used within a FilesProvider')
  }
  return context
}

interface FilesProviderProps {
  children: React.ReactNode
}

export const FilesProvider: React.FC<FilesProviderProps> = ({ children }) => {
  // Core state
  const [files, setFiles] = useState<LandFile[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [categories, setCategories] = useState<
    { category: FileCategory; count: number }[]
  >([])
  const [currentLandId, setCurrentLandId] = useState<string | null>(null)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState<FileCategory | 'all'>(
    'all'
  )

  // Fetch files for a specific land
  const fetchFiles = useCallback(async (landId: string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentLandId(landId)

      const response = await filesService.getByLandId(landId)
      setFiles(response.files)
      setTotal(response.total)
      setCategories(response.categories)
    } catch (err) {
      console.error('Failed to fetch files:', err)
      setError('Failed to load files')
      setFiles([])
      setTotal(0)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh files for current land
  const refreshFiles = useCallback(async () => {
    if (currentLandId) {
      await fetchFiles(currentLandId)
    }
  }, [currentLandId, fetchFiles])

  // Upload a new file
  const uploadFile = useCallback(
    async (landId: string, file: File, description?: string) => {
      try {
        setLoading(true)
        setError(null)

        await filesService.uploadFile(landId, file, description)

        // Refresh files after upload
        await refreshFiles()
      } catch (err) {
        console.error('Failed to upload file:', err)
        setError('Failed to upload file')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [refreshFiles]
  )

  // Delete a file
  const deleteFile = useCallback(
    async (fileId: string) => {
      try {
        setLoading(true)
        setError(null)

        await filesService.deleteFile(fileId)

        // Refresh files after deletion
        await refreshFiles()
      } catch (err) {
        console.error('Failed to delete file:', err)
        setError('Failed to delete file')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [refreshFiles]
  )

  // Download a file
  const downloadFile = useCallback(async (fileId: string) => {
    try {
      await filesService.downloadFile(fileId)
    } catch (err) {
      console.error('Failed to download file:', err)
      setError('Failed to download file')
      throw err
    }
  }, [])

  // Reset filters when land changes
  useEffect(() => {
    setSearchTerm('')
    setActiveCategory('all')
  }, [currentLandId])

  const contextValue: FilesContextState = {
    // Data
    files,
    loading,
    error,
    total,
    categories,

    // Filters
    searchTerm,
    setSearchTerm,
    activeCategory,
    setActiveCategory,

    // Actions
    fetchFiles,
    refreshFiles,
    uploadFile,
    deleteFile,
    downloadFile,

    // Current land ID
    currentLandId,
  }

  return (
    <FilesContext.Provider value={contextValue}>
      {children}
    </FilesContext.Provider>
  )
}
